import React from 'react';
import { SwordGlyph, ShieldGlyph, HeartGlyph } from '../CombatPlayerbar/ResultPillIcons';
import './GameTip.css';

/* GameTip mechanic-glyph vocabulary — the tooltip canon (Edward 2026-06-11).

   One semantic key → one glyph → one color, EVERYWHERE. A tooltip never says
   "deals 18 damage"; it says `⚔ 18` (sword glyph + value). The same sword that
   labels the damage result pill labels damage in every tooltip body, so the
   player is trained once and reads everything.

   This is MECHANIC vocabulary (damage / block / hp / rounds / focus / lock /
   tap / dead). The drug- and threat-specific STATUS vocabulary (ketone,
   histamine, plaque…) stays in CombatPlayerbar/statusWordGlyphs.jsx — the two
   compose: statusWordGlyphs handles word→glyph in free prose, MECH handles
   structured stat rows.

   Canon spec: wiki/decisions/2026-06-11-tooltip-canon.md */

const svgIcon = (children) => ({ size = 13, className }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

// Time-in-rounds — same clock face as the "delayed" threat tag, because both
// mean "this lands later".
const ClockIcon = svgIcon(<>
  <circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5l3.2 2.2" />
</>);

// Focus / targeting — crosshair.
const CrosshairIcon = svgIcon(<>
  <circle cx="12" cy="12" r="6.5" />
  <path d="M12 2.5v4" /><path d="M12 17.5v4" /><path d="M2.5 12h4" /><path d="M17.5 12h4" />
  <circle cx="12" cy="12" r="0.5" fill="currentColor" />
</>);

// Tap / click affordance — pointer finger on a tap ring.
const TapIcon = svgIcon(<>
  <path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12" />
  <path d="M11 11.5v-1a1.5 1.5 0 0 1 3 0V12" />
  <path d="M14 11.5a1.5 1.5 0 0 1 3 0V13" />
  <path d="M17 12.5a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-2c-2.1 0-3.4-.7-4.4-2L4.3 15.6a1.5 1.5 0 0 1 2.4-1.8L8 15.5" />
</>);

// Locked — padlock.
const LockIcon = svgIcon(<>
  <rect x="5" y="11" width="14" height="9" rx="2" />
  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
</>);

// Defeated — skull.
const SkullIcon = svgIcon(<>
  <path d="M12 3a8 8 0 0 1 8 8c0 2.5-1.2 4.6-3 6v3a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-3c-1.8-1.4-3-3.5-3-6a8 8 0 0 1 8-8z" />
  <circle cx="9" cy="11.5" r="1" fill="currentColor" /><circle cx="15" cy="11.5" r="1" fill="currentColor" />
  <path d="M12 16v2" />
</>);

// Direction modifiers — the "which way does this number move" marks. A reduce
// (↓) or gain (↑) rides in FRONT of the mechanic it modifies: `↓ ⚔ 15%` reads
// "cuts your damage 15%". Drawn as bold single arrows in the muted modifier
// slate so they read as grammar, not as their own stat (Edward 2026-07-12).
const DownIcon = svgIcon(<>
  <path d="M12 5v13" /><path d="M6 12l6 6 6-6" />
</>);
const UpIcon = svgIcon(<>
  <path d="M12 19V6" /><path d="M6 12l6-6 6 6" />
</>);

/* The vocabulary. Key → { color, Icon }. Colors are the SAME ones the matching
   HUD element already uses (result pills, lock glyph gold, etc.) so the glyph
   reads identically wherever it appears. Extend here, never inline. */
export const MECH = {
  damage: { color: 'rgb(248, 113, 113)', Icon: SwordGlyph },
  block: { color: 'rgb(148, 163, 184)', Icon: ShieldGlyph },
  hp: { color: 'rgb(74, 222, 128)', Icon: HeartGlyph },
  rounds: { color: 'rgb(45, 212, 191)', Icon: ClockIcon },
  focus: { color: '#ef4444', Icon: CrosshairIcon },
  tap: { color: 'rgba(255, 255, 255, 0.85)', Icon: TapIcon },
  lock: { color: 'rgb(251, 191, 36)', Icon: LockIcon },
  dead: { color: 'rgb(107, 114, 128)', Icon: SkullIcon },
  // Direction modifiers (muted slate — grammar, not a stat of their own).
  down: { color: 'rgb(148, 163, 184)', Icon: DownIcon },
  up: { color: 'rgb(148, 163, 184)', Icon: UpIcon },
};

/* Damage-sword OWNER split (Edward 2026-07-12). A `damage` stat can describe the
   ENEMY's attack (red, the default combat threat color) or YOUR strike (blue —
   the same friendly blue the keystone/player accents use). The def author tags
   the token `{ g: 'damage', owner: 'enemy' | 'player' }`; StatRow resolves the
   color from here so "histamine ⚔+2" reads as an enemy buff and "deposit ↓⚔15%"
   reads as a cut to the player's own strike. */
export const DMG_OWNER_COLOR = {
  enemy: 'rgb(248, 113, 113)',
  player: 'rgb(96, 165, 250)',
};

/* The causal arrow — "becomes / causes / leads to". The single most repeated
   depiction in the decode grammar (Edward 2026-06-11: custom element, not a
   unicode char), drawn in the house stroke style. Any '→' in a decode join or
   fragment renders as this element via renderArrows() — authors keep writing
   the literal arrow, the renderer upgrades it. */
export const ArrowGlyph = ({ size = 13, className }) => (
  <svg
    className={['gt-arrow__svg', className].filter(Boolean).join(' ')}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12h15" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

/* A LABELED causal edge — the relationship verb sits ON the arrow line, and the
   line extends to fit the word (Edward 2026-06-11: "hardens sits right on the
   arrow line and the arrow dynamically extends to fit the word naming its
   edge"). Built as a flex row — a rail, the word, then a rail ending in an
   arrowhead — so the connector's width grows with the label automatically.
   Used via the decode token `{ edge: 'Bites' }` → "longer it drags —Bites→ harder"
   (`phantom`, announceDecode.js). ⚠️ It used to read `{ edge: 'Hardens' }` on eldra
   and athero; both converter rows took the plain-word `into` form on 2026-08-15, so
   `phantom` is now the only authored edge in the game. The glyph is unchanged and
   still reusable — this note is only so the example names a row that exists. */
export function EdgeGlyph({ label, className }) {
  return (
    <span
      className={['gt-edge', className].filter(Boolean).join(' ')}
      aria-label={label ? `${label}, leads to` : 'leads to'}
    >
      <span className="gt-edge__rail" aria-hidden="true" />
      {label != null && label !== '' && <span className="gt-edge__label">{label}</span>}
      <span className="gt-edge__rail gt-edge__rail--head" aria-hidden="true" />
    </span>
  );
}

/* Split a copy string on literal '→' and interleave the ArrowGlyph element.
   Returns the string untouched (cheap path) when no arrow is present. */
export function renderArrows(text) {
  const s = String(text ?? '');
  if (!s.includes('→')) return s;
  const parts = s.split('→');
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="gt-arrow" aria-label="leads to"><ArrowGlyph /></span>}
          {part.trim()}
        </React.Fragment>
      ))}
    </>
  );
}

/* An HP number rendered as a status-tag CHIP, inline in decode text (Edward
   2026-07-14). The reference is the Ten Shin aura's `+5`: an HP value that used to
   paint as a bare heart-glyph now wears the SAME light-chip chrome as the status
   tags, so HP numbers read as first-class tags in the tooltip grammar and reuse
   everywhere. It reuses the `bls-status-header--tab` classes (styled by the
   `.combat-status-rail` ancestor every decode row carries → pixel-identical to the
   sibling status chips, and the tooltip's own `min-width:0` lets it hug its short
   value) plus the MECH.hp heart. Author it with `{ g:'hp', v, chip:true }` (helper
   `hp(v)` in announceDecode.js). HP BARS are untouched — their tooltips keep the
   bare glyph by simply not setting the chip flag. */
export function HpChip({ value, color }) {
  const Icon = MECH.hp.Icon;
  return (
    <span
      className="bls-status-header bls-status-header--tab bls-status-header--hp gt-hp-chip"
      style={color ? { color } : undefined}
    >
      {Icon && <Icon size={12} className="bls-status-header-icon" />}
      {value !== undefined && <span className="bls-status-tab-value">{value}</span>}
    </span>
  );
}

/* One mechanic glyph, anywhere (e.g. the lock in PillCard's requirement row). */
export function MechGlyph({ g, size = 13, className }) {
  const mech = MECH[g];
  if (!mech) return null;
  const { Icon, color } = mech;
  return (
    <span className={['gt-glyph', className].filter(Boolean).join(' ')} style={{ color }}>
      <Icon size={size} />
    </span>
  );
}

/* A stat row — the canonical glyph-stat grammar line.
   items: array of
     { g: 'hp', v: '45/60' }   → glyph + value pair, in the glyph's canon color
     { g: 'focus', v: 'focused', accent: true } → pair tinted to the host accent
     { j: 'when' }             → muted connective ("when", "on", "—")
     { t: 'focus fire' }       → plain text fragment
   Falsy items are skipped so callers can branch inline. */
export function StatRow({ items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <span className="gt-statrow">
      {list.map((it, i) => {
        if (it.edge !== undefined) return <EdgeGlyph key={i} label={it.edge} />;
        if (it.j) return <span key={i} className="gt-join">{renderArrows(it.j)}</span>;
        if (it.t !== undefined) return <span key={i} className={it.italic ? 'gt-text gt-text--italic' : 'gt-text'}>{renderArrows(it.t)}</span>;
        // HP number as a status-tag chip (chip:true) — inline tooltip HP values.
        if (it.g === 'hp' && it.chip) return <HpChip key={i} value={it.v} color={it.color} />;
        const mech = MECH[it.g];
        const Icon = mech?.Icon;
        // Owner-colored damage sword (enemy red / player blue) when the token
        // carries an owner; otherwise the mechanic's canon color.
        const ownerColor = it.g === 'damage' && it.owner ? DMG_OWNER_COLOR[it.owner] : undefined;
        const color = it.accent
          ? 'var(--effect-color, #fbbf24)'
          : (it.color || ownerColor || mech?.color);
        return (
          <span key={i} className="gt-stat" style={color ? { color } : undefined}>
            {/* `icon` carries a pre-built element (e.g. a status-tag glyph from
                a decode line); `g` resolves from the MECH vocabulary. */}
            {it.icon || (Icon && <Icon size={13} className="gt-stat__icon" />)}
            {it.v !== undefined && <span className="gt-stat__v">{it.v}</span>}
          </span>
        );
      })}
    </span>
  );
}

/* Flatten a stat-row items array to plain text for aria-labels.
   `{g:'hp', v:'45/60'}` → "hp 45/60"; joins and fragments pass through. */
export function statsToText(items) {
  return (items || [])
    .filter(Boolean)
    .map((it) => {
      if (it.edge !== undefined) return `${it.edge} →`;
      if (it.j) return it.j;
      if (it.t !== undefined) return it.t;
      return [it.g, it.v].filter((x) => x !== undefined).join(' ');
    })
    .join(' ')
    .trim();
}
